import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonPage,
  IonToolbar,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { ActionIcon, Text, Title } from "@mantine/core";
import { useCallback, useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import { useHistory } from "react-router-dom";
import {
  MIGRATION_FULLPAGE_DISMISSED,
  MIGRATION_FULLPAGE_VIEWED,
} from "../constants/posthogEvents";
import SmdMigrationSupportActions from "../components/SmdMigrationSupportActions";
import type { MigrationEntryPoint } from "../utils/smdMigrationInterception";
import styles from "./SmdMigrationContactPage.module.css";

const SmdMigrationContactPage = ({
  entryPoint,
}: {
  entryPoint: MigrationEntryPoint;
}) => {
  const posthog = usePostHog();
  const history = useHistory();
  const pageRef = useRef<HTMLElement>(null);
  const activeRef = useRef(false);
  const dismissedRef = useRef(false);
  const ctaUsedRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousTitleRef = useRef<string>();
  const focusFrameRef = useRef<number>();

  const activatePage = useCallback(() => {
    if (activeRef.current) return;

    activeRef.current = true;
    dismissedRef.current = false;
    ctaUsedRef.current = false;
    posthog.capture(MIGRATION_FULLPAGE_VIEWED, {
      entry_point: entryPoint,
    });

    previousTitleRef.current = document.title;
    document.title = "FuturHealth+ | Account migration";
    focusFrameRef.current = window.requestAnimationFrame(() =>
      headingRef.current?.focus()
    );
  }, [entryPoint, posthog]);

  const deactivatePage = useCallback(() => {
    activeRef.current = false;

    if (focusFrameRef.current) {
      window.cancelAnimationFrame(focusFrameRef.current);
      focusFrameRef.current = undefined;
    }

    if (previousTitleRef.current !== undefined) {
      document.title = previousTitleRef.current;
      previousTitleRef.current = undefined;
    }
  }, []);

  useIonViewDidEnter(activatePage, [activatePage]);
  useIonViewWillLeave(deactivatePage, [deactivatePage]);

  useEffect(() => {
    // The access query may resolve after Ionic has already emitted did-enter.
    // Activate only when this newly mounted page belongs to the visible view.
    const frame = window.requestAnimationFrame(() => {
      if (!pageRef.current?.closest(".ion-page-hidden")) activatePage();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      deactivatePage();
    };
  }, [activatePage, deactivatePage]);

  const captureDismissed = useCallback(() => {
    if (dismissedRef.current || ctaUsedRef.current) return;

    dismissedRef.current = true;
    posthog.capture(MIGRATION_FULLPAGE_DISMISSED, {
      entry_point: entryPoint,
    });
  }, [entryPoint, posthog]);

  useEffect(
    () =>
      history.listen((_nextLocation, action) => {
        if (action === "POP" && activeRef.current) captureDismissed();
      }),
    [captureDismissed, history]
  );

  const handleDismiss = () => {
    captureDismissed();

    if (history.action === "PUSH") {
      history.goBack();
      return;
    }

    history.replace("/tabs/home");
  };

  return (
    <IonPage
      ref={pageRef}
      className={styles.page}
      data-testid="migration-contact-page"
    >
      <IonHeader mode="ios" className={styles.header}>
        <IonToolbar className={styles.toolbar}>
          <img className={styles.logo} src="/full-logo.svg" alt="FuturHealth" />
          <div slot="end" className={styles.closeAction}>
            <ActionIcon
              variant="subtle"
              color="gray"
              radius="xl"
              size={44}
              className={styles.closeButton}
              aria-label="Close migration information"
              onClick={handleDismiss}
              data-testid="migration-fullpage-dismiss"
            >
              <FontAwesomeIcon icon={faXmark} />
            </ActionIcon>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className={styles.content}>
        <main className={styles.shell}>
          <section className={styles.copyColumn}>
            <div>
              <Title
                order={1}
                ref={headingRef}
                tabIndex={-1}
                ff="var(--font-instrument)"
                fw={400}
              >
                Meet FuturHealth+
                <br />
                your care, upgraded
              </Title>
              <Text
                component="p"
                className={styles.intro}
                ff="var(--font-inter)"
              >
                FuturHealth+ is our new platform, with everything you use today
                rebuilt on better technology. It&apos;s quicker, smoother, and
                takes fewer taps to get where you&apos;re going. Our support
                team will move your account over for you, start to finish.
              </Text>
            </div>

            <div className={styles.setup}>
              <Text component="h2" ff="var(--font-inter)">
                Two ways to get set up
              </Text>
              <Text component="p" ff="var(--font-inter)">
                Either way, our team will move you over and answer anything
                you&apos;re wondering about.
              </Text>
              <div className={styles.inlineActions}>
                <SmdMigrationSupportActions
                  placement="fullpage"
                  entryPoint={entryPoint}
                  onCtaClick={() => {
                    ctaUsedRef.current = true;
                  }}
                />
              </div>
            </div>
          </section>

          <section
            className={styles.preview}
            aria-label="FuturHealth+ app preview"
          >
            <img
              src="/images/smd-migration-phone.webp"
              width={960}
              height={1141}
              alt="FuturHealth+ mobile app screens"
            />
            <div className={styles.rating} aria-label="Rated 4.8 out of 5">
              <span aria-hidden="true">★</span> 4.8
            </div>
          </section>
        </main>
      </IonContent>
      <IonFooter className={styles.mobileFooter}>
        <SmdMigrationSupportActions
          placement="fullpage"
          entryPoint={entryPoint}
          testIdSuffix="mobile"
          onCtaClick={() => {
            ctaUsedRef.current = true;
          }}
        />
      </IonFooter>
    </IonPage>
  );
};

export default SmdMigrationContactPage;
